import { useEffect, useState } from "react";
import api, { rupiah, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Modal } from "@/pages/Settings";
import { Calculator, Download, FileText, Search, Pencil, Plus, CalendarRange, Clock3, LogOut, X, Upload, Trash2 } from "lucide-react";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const M_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const PTKP = ["TK/0","TK/1","TK/2","TK/3","K/0","K/1","K/2","K/3"];

export default function Payroll() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [yearly, setYearly] = useState(null);
  const [a1, setA1] = useState(null);
  const [history, setHistory] = useState(null);
  const [preview, setPreview] = useState(null);
  const openHistory = async (r) => { try { const res = await api.get(`/payroll/history?user_id=${r.user_id}&year=${year}`); setHistory({ name: r.name, rows: res.data }); } catch (e) { toast.error("Failed"); } };
  const confirmImport = async () => { try { const r = await api.post("/payroll/import-confirm", { month, year, rows: preview.rows }); toast.success(`Imported ${r.data.imported} row(s)`); setPreview(null); load(); } catch (e) { toast.error("Import failed"); } };

  const del = async (id) => { if (!window.confirm("Delete this payroll record?")) return; try { await api.delete(`/payroll/${id}`); toast.success("Payroll deleted"); load(); } catch (e) { toast.error("Failed"); } };
  const computeA1 = async () => {
    try { const r = await api.get(`/payroll/a1-recap?user_id=${form.user_id}&year=${year}&month=${month}`); setA1(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "No prior records"); }
  };

  const load = () => api.get(`/payroll?month=${month}&year=${year}`).then((r) => setRecords(r.data));
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [month, year]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);

  const openFor = (rec, u) => {
    setForm(rec ? { ...rec } : {
      user_id: u.id, month, year, base_salary: 0, positional_allowance: 0, transport_allowance: 0,
      meal_allowance: 0, other_allowance: 0, thr: 0, bonus: 0, bpjs_base_salary: 0,
      rate_kesehatan: 1, rate_jht: 2, rate_jp: 1, bpjs_kesehatan: 0, bpjs_tk: 0,
      ptkp_status: "TK/0", auto_bpjs: true, prorate_attendance: false, standard_working_days: 22,
      kpi_hours_threshold: 170, kpi_points: 5, kpi_point_value: 20000, is_resign: false, a1_annualize: true, separate_bonus_tax: false,
    });
    setA1(null);
    setOpen(true);
  };

  const save = async () => {
    try { await api.post("/payroll", { ...form, month, year }); toast.success("Payroll saved"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const openYearly = async () => {
    const r = await api.get(`/payroll-summary/year?year=${year}`);
    setYearly(r.data);
  };

  const existingIds = records.map((r) => r.user_id);
  const missing = users.filter((u) => !existingIds.includes(u.id));
  const filtered = records.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[var(--gold)] text-xs uppercase tracking-[0.2em] font-semibold">Feature 5 · SPV Only</div>
          <h1 className="font-serif text-4xl mt-1">Payroll · Tax · BPJS</h1>
          <p className="text-[var(--text-secondary)] mt-1">Auto PPh 21 (Art. 21 / A1), attendance-based, KPI bonus · IDR</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
            <input data-testid="payroll-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 pr-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)] w-36" />
          </div>
          <select data-testid="payroll-month-select" value={month} onChange={(e) => setMonth(+e.target.value)} className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(+e.target.value)} className="px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none">
            {[year - 1, year, year + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="payroll-yearly-view-button" onClick={openYearly} className="px-4 py-2 text-sm rounded-lg border border-[var(--border-gold)] bg-[var(--gold-light)] text-[var(--gold-hover)] flex items-center gap-2 hover:bg-[var(--gold)] hover:text-white transition"><CalendarRange className="w-4 h-4" /> Yearly</button>
          <button data-testid="payroll-import-button" onClick={() => document.getElementById("payroll-import-file").click()} className="px-4 py-2 text-sm rounded-lg border border-[var(--border-gold)] bg-[var(--gold-light)] text-[var(--gold-hover)] flex items-center gap-2 hover:bg-[var(--gold)] hover:text-white transition"><Upload className="w-4 h-4" /> Import</button>
          <input id="payroll-import-file" data-testid="payroll-import-file" type="file" accept=".xlsx" className="hidden" onChange={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            const fd = new FormData(); fd.append("file", f);
            try { const r = await api.post("/payroll/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPreview(r.data); }
            catch (err) { toast.error(err.response?.data?.detail || "Parse failed"); }
            e.target.value = "";
          }} />
          <button data-testid="export-excel-payroll-button" onClick={() => downloadFile(`/export/payroll?month=${month}&year=${year}`, `${String(month).padStart(2,"0")} - SV Payroll Report ${year}.xlsx`)} className="btn-gold px-4 py-2 text-sm flex items-center gap-2"><Download className="w-4 h-4" /> Excel</button>
        </div>
      </div>

      <div className="card-elegant overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[var(--text-muted)] text-xs uppercase bg-[var(--bg-subtle)]">
              <th className="py-3 px-5">Employee</th><th>Days / Hrs</th><th>KPI</th><th>Gross</th><th>BPJS</th><th>PPh 21</th><th>Net Salary</th><th className="text-right px-5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`payroll-row-${r.id_code}`} className="border-t border-[var(--border-subtle)] hover:bg-[var(--gold-light)]/40">
                <td className="py-3 px-5">
                  <div className="font-medium">{r.name} {r.is_resign && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-[var(--urgent)] ml-1">A1 Resign</span>}</div>
                  <div className="text-xs font-mono text-[var(--text-muted)]">{r.id_code} · {r.ptkp_status}</div>
                </td>
                <td className="font-mono text-xs">{r.attendance_days}d / {r.worked_hours}h</td>
                <td className="font-mono text-xs">{r.kpi_points_awarded ? <span className="text-[var(--complete)]">+{r.kpi_points_awarded}pts</span> : "—"}</td>
                <td className="font-mono">{rupiah(r.gross)}</td>
                <td className="font-mono text-xs">{rupiah(r.total_bpjs)}</td>
                <td className="font-mono text-[var(--urgent)]">{rupiah(r.pph21)}</td>
                <td className="font-mono font-semibold text-[var(--complete)]">{rupiah(r.net)}</td>
                <td className="text-right px-5 whitespace-nowrap">
                  <button onClick={() => openFor(r)} className="text-[var(--text-muted)] hover:text-[var(--gold)] p-1.5" title="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => downloadFile(`/payroll/${r.id}/slip`, `${String(month).padStart(2,"0")} - SV SalarySlip ${r.name}.pdf`)} data-testid={`download-salary-slip-pdf-button-${r.id_code}`} className="inline-flex text-[var(--text-muted)] hover:text-[var(--teal)] p-1.5" title="Download PDF slip"><FileText className="w-4 h-4" /></button>
                  <button data-testid={`delete-payroll-${r.id_code}`} onClick={() => del(r.id)} className="text-[var(--text-muted)] hover:text-[var(--urgent)] p-1.5" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  <button data-testid={`payroll-history-${r.id_code}`} onClick={() => openHistory(r)} className="text-[var(--text-muted)] hover:text-[var(--gold)] p-1.5" title="Salary history"><CalendarRange className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="8" className="py-8 text-center text-[var(--text-muted)]">No payroll for this month. Add employees below.</td></tr>}
          </tbody>
        </table>
      </div>

      {missing.length > 0 && (
        <div className="card-elegant p-5">
          <div className="text-sm font-semibold text-[var(--text-secondary)] mb-3">Add employee to {MONTHS[month - 1]} {year} payroll</div>
          <div className="flex flex-wrap gap-2">
            {missing.map((u) => (
              <button key={u.id} data-testid={`add-payroll-${u.id_code}`} onClick={() => openFor(null, u)} className="px-3 py-1.5 rounded-lg border border-[var(--border-gold)] bg-[var(--gold-light)] text-sm flex items-center gap-1.5 hover:bg-[var(--gold)] hover:text-white transition">
                <Plus className="w-3.5 h-3.5" /> {u.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {open && form && (
        <Modal wide title="Payroll Entry" onClose={() => setOpen(false)}>
          <div className="grid grid-cols-2 gap-4">
            <NumField label="Base Salary" value={form.base_salary} onChange={(v) => setForm({ ...form, base_salary: v })} testid="payroll-base-salary" />
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">PTKP Status</label>
              <select value={form.ptkp_status} onChange={(e) => setForm({ ...form, ptkp_status: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none focus:border-[var(--gold)]">
                {PTKP.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <NumField label="Positional Allowance" value={form.positional_allowance} onChange={(v) => setForm({ ...form, positional_allowance: v })} />
            <NumField label="Transport Allowance" value={form.transport_allowance} onChange={(v) => setForm({ ...form, transport_allowance: v })} />
            <NumField label="Meal Allowance" value={form.meal_allowance} onChange={(v) => setForm({ ...form, meal_allowance: v })} />
            <NumField label="Other Allowance" value={form.other_allowance} onChange={(v) => setForm({ ...form, other_allowance: v })} />
            <NumField label="THR (Holiday Allowance)" value={form.thr} onChange={(v) => setForm({ ...form, thr: v })} testid="payroll-thr" />
            <NumField label="Bonus" value={form.bonus} onChange={(v) => setForm({ ...form, bonus: v })} testid="payroll-bonus" />
          </div>

          {/* Attendance-based prorate */}
          <div className="mt-4 rounded-xl border border-[var(--border-subtle)] p-4">
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="payroll-prorate-toggle" type="checkbox" checked={form.prorate_attendance} onChange={(e) => setForm({ ...form, prorate_attendance: e.target.checked })} className="accent-[var(--gold)] w-4 h-4" />
              <Clock3 className="w-4 h-4 text-[var(--gold)]" /> Calculate work days from attendance (prorate base salary)
            </label>
            {form.prorate_attendance && (
              <div className="mt-3">
                <label className="text-xs font-semibold text-[var(--text-secondary)]">Standard Working Days / month</label>
                <input data-testid="payroll-working-days" type="number" min="1" max="31" value={form.standard_working_days} onChange={(e) => setForm({ ...form, standard_working_days: +e.target.value })} className="mt-1 w-40 px-3 py-2 rounded-lg border border-[var(--border-subtle)] font-mono text-sm outline-none focus:border-[var(--gold)]" />
              </div>
            )}
          </div>

          {/* BPJS from base + rates */}
          <div className="mt-4 rounded-xl border border-[var(--border-subtle)] p-4 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input type="checkbox" checked={form.auto_bpjs} onChange={(e) => setForm({ ...form, auto_bpjs: e.target.checked })} className="accent-[var(--gold)] w-4 h-4" />
              Calculate BPJS from a BPJS Base Salary
            </label>
            {form.auto_bpjs ? (
              <div className="grid grid-cols-4 gap-3">
                <NumField label="BPJS Base Salary" value={form.bpjs_base_salary} onChange={(v) => setForm({ ...form, bpjs_base_salary: v })} testid="payroll-bpjs-base" />
                <RateField label="Kesehatan %" value={form.rate_kesehatan} onChange={(v) => setForm({ ...form, rate_kesehatan: v })} />
                <RateField label="JHT %" value={form.rate_jht} onChange={(v) => setForm({ ...form, rate_jht: v })} />
                <RateField label="JP %" value={form.rate_jp} onChange={(v) => setForm({ ...form, rate_jp: v })} />
                <div className="col-span-4 text-xs text-[var(--text-secondary)]">Base defaults to (prorated) base salary if left 0. Rates are adjustable.</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <NumField label="BPJS Kesehatan (manual)" value={form.bpjs_kesehatan} onChange={(v) => setForm({ ...form, bpjs_kesehatan: v })} />
                <NumField label="BPJS Ketenagakerjaan (manual)" value={form.bpjs_tk} onChange={(v) => setForm({ ...form, bpjs_tk: v })} />
              </div>
            )}
          </div>

          {/* Bonus tax split */}
          <label className="flex items-center gap-2 mt-4 text-sm rounded-xl border border-[var(--border-subtle)] p-4">
            <input data-testid="payroll-separate-bonus-tax" type="checkbox" checked={form.separate_bonus_tax} onChange={(e) => setForm({ ...form, separate_bonus_tax: e.target.checked })} className="accent-[var(--gold)] w-4 h-4" />
            Tax THR &amp; Bonus separately from monthly salary (more accurate irregular-income withholding)
          </label>

          {/* KPI bonus */}
          <div className="mt-4 rounded-xl border border-[var(--border-subtle)] p-4">
            <div className="text-sm font-semibold mb-3">KPI Hours Bonus</div>
            <div className="grid grid-cols-3 gap-3">
              <RateField label="Hours Threshold" value={form.kpi_hours_threshold} onChange={(v) => setForm({ ...form, kpi_hours_threshold: v })} testid="payroll-kpi-threshold" />
              <RateField label="Points Awarded" value={form.kpi_points} onChange={(v) => setForm({ ...form, kpi_points: v })} testid="payroll-kpi-points" />
              <NumField label="Value / Point" value={form.kpi_point_value} onChange={(v) => setForm({ ...form, kpi_point_value: v })} testid="payroll-kpi-value" />
            </div>
            <div className="text-xs text-[var(--text-secondary)] mt-2">If monthly worked hours &gt; threshold, employee earns the points × value as bonus.</div>
          </div>

          {/* Resign A1 */}
          <div className="mt-4 rounded-xl border border-[var(--border-subtle)] p-4">
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="payroll-resign-toggle" type="checkbox" checked={form.is_resign} onChange={(e) => setForm({ ...form, is_resign: e.target.checked })} className="accent-[var(--urgent)] w-4 h-4" />
              <LogOut className="w-4 h-4 text-[var(--urgent)]" /> Resigning employee — build A1 (1721-A1) year-end tax
            </label>
            {form.is_resign && (
              <div className="mt-3 space-y-3">
                <label className="flex items-center gap-2 text-sm">
                  <input data-testid="payroll-a1-annualize" type="checkbox" checked={form.a1_annualize} onChange={(e) => setForm({ ...form, a1_annualize: e.target.checked })} className="accent-[var(--gold)] w-4 h-4" />
                  PTKP / income <strong>disetahunkan</strong> (annualized) — untick for tidak disetahunkan (actual YTD)
                </label>
                <button data-testid="payroll-compute-a1" type="button" onClick={computeA1} className="px-4 py-2 text-sm rounded-lg border border-[var(--border-gold)] bg-[var(--gold-light)] text-[var(--gold-hover)] flex items-center gap-2 hover:bg-[var(--gold)] hover:text-white transition"><Calculator className="w-4 h-4" /> Hitung A1 dari bulan sebelumnya (Jan–{MONTHS[month - 1]})</button>
                {form.id && <button data-testid="download-a1-pdf" type="button" onClick={() => downloadFile(`/payroll/${form.id}/a1-pdf?annualize=${form.a1_annualize}`, `SV Form1721-A1 ${form.name}.pdf`)} className="btn-gold px-4 py-2 text-sm flex items-center gap-2"><FileText className="w-4 h-4" /> Download Form 1721-A1 (PDF)</button>}
                {a1 && (
                  <div data-testid="a1-result" className="rounded-xl bg-[var(--bg-subtle)] p-4 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-[var(--text-secondary)]">Months worked</span><span className="font-mono">{a1.months_worked}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--text-secondary)]">Accumulated gross (YTD)</span><span className="font-mono">{rupiah(a1.accumulated_gross)}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--text-secondary)]">PTKP ({a1.ptkp_status})</span><span className="font-mono">{rupiah(a1.ptkp)}</span></div>
                    <div className="border-t border-[var(--border-subtle)] my-2" />
                    <div className={`flex justify-between ${form.a1_annualize ? "font-bold text-[var(--gold-hover)]" : ""}`}><span>PPh 21 A1 — Disetahunkan</span><span className="font-mono">{rupiah(a1.annualized.tax_due_period)}</span></div>
                    <div className={`flex justify-between ${!form.a1_annualize ? "font-bold text-[var(--gold-hover)]" : ""}`}><span>PPh 21 A1 — Tidak disetahunkan</span><span className="font-mono">{rupiah(a1.non_annualized.tax_due)}</span></div>
                    <div className="text-xs text-[var(--text-muted)] pt-1">Selected method ({form.a1_annualize ? "disetahunkan" : "tidak disetahunkan"}) is the recommended A1 figure for this resignation.</div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-5 rounded-xl bg-[var(--bg-subtle)] p-4 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
            <Calculator className="w-4 h-4 text-[var(--gold)]" /> PPh 21, BPJS, KPI bonus & net salary are calculated automatically on save.
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-[var(--border-subtle)]">Cancel</button>
            <button data-testid="payroll-save-button" onClick={save} className="btn-gold px-5 py-2 text-sm">Save & Calculate</button>
          </div>
        </Modal>
      )}

      {yearly && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(17,19,24,0.5)" }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-auto fade-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)] sticky top-0 bg-white">
              <h3 className="font-serif text-xl">Yearly Payroll — {year} (Net per month, IDR)</h3>
              <div className="flex items-center gap-2">
                <button data-testid="export-payroll-year-button" onClick={() => downloadFile(`/export/payroll-year?year=${year}`, `SV Payroll Yearly Report ${year}.xlsx`)} className="btn-gold px-4 py-2 text-sm flex items-center gap-2"><Download className="w-4 h-4" /> Excel</button>
                <button onClick={() => setYearly(null)} className="text-[var(--text-muted)] hover:text-[var(--urgent)]"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[var(--text-muted)] uppercase">
                    <th className="py-2 px-2 sticky left-0 bg-white">Employee</th>
                    {M_ABBR.map((m) => <th key={m} className="px-2 text-right">{m}</th>)}
                    <th className="px-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {yearly.map((e) => (
                    <tr key={e.id_code} className="border-t border-[var(--border-subtle)]">
                      <td className="py-2 px-2 font-medium sticky left-0 bg-white">{e.name}</td>
                      {M_ABBR.map((m, i) => <td key={m} className="px-2 text-right font-mono">{e.months[i + 1] ? (e.months[i + 1].net / 1000).toFixed(0) + "k" : "—"}</td>)}
                      <td className="px-2 text-right font-mono font-semibold text-[var(--complete)]">{rupiah(e.total_net)}</td>
                    </tr>
                  ))}
                  {yearly.length === 0 && <tr><td colSpan="14" className="py-8 text-center text-[var(--text-muted)]">No payroll data for {year}.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-[var(--text-secondary)]">{label} (IDR)</label>
      <input data-testid={testid} type="number" value={value} onChange={(e) => onChange(+e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] font-mono text-sm outline-none focus:border-[var(--gold)]" />
    </div>
  );
}
function RateField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-[var(--text-secondary)]">{label}</label>
      <input data-testid={testid} type="number" step="0.1" value={value} onChange={(e) => onChange(+e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] font-mono text-sm outline-none focus:border-[var(--gold)]" />
    </div>
  );
}
